import { useLayoutEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useText } from "../../clients/words";
import { WordsProvider } from "../../clients/words-ui";
import { fetchPerepiskaZapisi, nachatPerepisku, type PerepiskaZapisi } from "../../zapis/api";
import { fetchHronikaKlientki, type Razbor as RazborDannye } from "../api";
import { useRazbor } from "../hooks";
import { LentaHroniki } from "./LentaHroniki";
import { Razbor } from "./Razbor";
import { ZnachokKanala } from "./ZnachokKanala";

/**
 * ОБЩИЙ ЧАТ — ОДИН ВХОД НА ВСЕ МЕСТА (`s720`, приёмка 06.10.2026: «один визуальный чат во всех
 * местах»). Контракт для хозяев мест — `docs/05-tasks/koordinaciya-paketa-s719-s723-20261006.md`,
 * раздел 4.
 *
 * Внутри — тот же `Razbor`: лента, подписи сообщений, полоса «кому / мессенджер / наша линия»,
 * поле ответа до пяти строк, черновик по беседе, шаблоны, выбор линии, блокировка редактирования.
 * Место задаёт только то, что вокруг: у «Переписок» своя шапка с ходами, у карточки лида ходы
 * свои, у мини-чата уведомления шапки нет вовсе.
 *
 * ЧТО ДАЁТ ХОЗЯИН МЕСТА: коробку с определённой высотой (flex-колонка, `min-height: 0`,
 * `overflow: hidden`) и ключ диалога — номер обращения. Своей прокрутки вокруг чата не нужно: ленту
 * крутит компонент. Одежду `ops-skin` компонент ставит на себя сам.
 *
 * БЕЗ ДИАЛОГА (`obrashchenieId === null`, карточка записи): пустоты и «разговора без поля ввода»
 * нет. Компонент показывает прежнюю переписку клиентки (сквозная хроника) и наши линии, с которых
 * ей можно написать, — каждую номером и принадлежностью. Выбор линии открывает диалог и зовёт
 * `onOtkryt`; дальше — обычный чат с полем. Ничего не отправляется, пока человек не напишет сам.
 */
export type MestoChata = "perepiski" | "lid" | "zapis" | "mini";

export interface ObshchiyChatProps {
  /** Ключ диалога — номер обращения; `null` — диалога ещё нет. */
  obrashchenieId: number | null;
  mesto: MestoChata;
  /** Разбор уже у хозяина (карточка лида держит его сама) — передаётся; иначе чат грузит сам. */
  dannye?: RazborDannye;
  gruzitsya?: boolean;
  /** Чья переписка, когда диалога нет. Без клиентки писать некому — чат говорит это словами. */
  bezDialoga?: { customerId: number | null; bookingId?: number | null; pochemu?: string | null };
  /** Чат сменил диалог (другая линия, начатая переписка) — хозяин меняет свой ключ. */
  onOtkryt?: (id: number) => void;
  onNazad?: () => void;
  onKlientka?: () => void;
  klientkaSlovo?: string;
  klientkaRyadom?: boolean;
  klientkaVOkne?: boolean;
  vneOtbora?: { onZakryt: () => void } | null;
}

export function ObshchiyChat({ obrashchenieId, mesto, dannye, gruzitsya, bezDialoga, onOtkryt, ...prochee }: ObshchiyChatProps) {
  // Хозяин разбор не дал — грузим сами тем же хуком и тем же ключом кэша, что «Переписки».
  const svoy = useRazbor(dannye === undefined ? obrashchenieId : null);
  const razbor = dannye ?? svoy.data;

  return (
    <div className="ops-skin flex h-full min-h-0 flex-col text-ops-text" data-testid="obshchiy-chat" data-mesto={mesto} data-dialog={obrashchenieId ?? "net"}>
      {obrashchenieId !== null ? (
        // Слова чата едут с разбором: чат не зависит от того, обернул ли хозяин место своим словарём.
        <WordsProvider hints={razbor?.hints ?? {}} words={razbor?.words ?? { labels: {}, texts: {} }}>
          <Razbor
            dannye={razbor}
            gruzitsya={gruzitsya ?? svoy.isLoading}
            onOtkryt={onOtkryt}
            vstroen={mesto === "lid"}
            senderNearReply={mesto === "perepiski"}
            mini={mesto === "mini"}
            {...prochee}
          />
        </WordsProvider>
      ) : (
        <BezDialoga dannye={bezDialoga ?? { customerId: null }} onOtkryt={onOtkryt} />
      )}
    </div>
  );
}

const ZAPASNYE: Record<string, string> = {
  "obrashcheniya.net_dialoga": "Переписки с этой клиенткой ещё не было.",
  "obrashcheniya.net_dialoga_nachat": "Выберите линию — откроется переписка, и можно писать.",
  "obrashcheniya.net_dialoga_nekuda": "Написать некуда: у клиентки нет адреса ни в одном мессенджере, где у нас есть своя линия.",
  "obrashcheniya.net_dialoga_bez_klientki": "У записи нет клиентки — писать некому.",
};

function BezDialoga({ dannye, onOtkryt }: { dannye: NonNullable<ObshchiyChatProps["bezDialoga"]>; onOtkryt?: (id: number) => void }) {
  const izSlovarya = useText();
  // Словарь места может не знать слов чата (карточка записи возит свой) — тогда говорит запасное слово.
  const text = (kod: string) => {
    const slovo = izSlovarya(kod);

    return slovo === kod ? (ZAPASNYE[kod] ?? kod) : slovo;
  };
  const qc = useQueryClient();
  const { customerId, bookingId = null } = dannye;
  const okno = useRef<HTMLDivElement>(null);

  // Тот же ключ кэша, что у карточки записи: её счётчик и это окно читают один ответ.
  const perepiska = useQuery<PerepiskaZapisi>({
    queryKey: ["zapis", "perepiska", bookingId],
    queryFn: () => fetchPerepiskaZapisi(bookingId as number),
    enabled: bookingId !== null,
    placeholderData: (prev) => prev,
  });
  const hronika = useQuery({
    queryKey: ["klient-hronika", customerId],
    queryFn: () => fetchHronikaKlientki(customerId as number, null),
    enabled: customerId !== null,
  });
  const nachat = useMutation({
    mutationFn: (l: { kind: string; channel_id: number }) => nachatPerepisku(bookingId as number, l.kind, l.channel_id),
    onSuccess: (otvet) => {
      qc.setQueryData(["zapis", "perepiska", bookingId], otvet);
      void qc.invalidateQueries({ queryKey: ["obrashcheniya"] });
      if (otvet.obrashchenie_id !== null) onOtkryt?.(otvet.obrashchenie_id);
    },
  });

  const estIstoriya = (hronika.data?.stroki.length ?? 0) > 0;
  useLayoutEffect(() => {
    if (estIstoriya && okno.current !== null) okno.current.scrollTop = okno.current.scrollHeight;
  }, [estIstoriya, customerId]);

  // Новый сервер называет все линии номером; прежний — по одной на мессенджер.
  const linii = perepiska.data?.linii
    ?? (perepiska.data?.kuda_pisat ?? []).map((k) => ({ channel_id: k.channel_id, kind: k.kind, nazvanie: k.kind_display, podpis: "", filial: "" }));
  const otkaz = (nachat.error as { response?: { data?: { message?: string } } } | null)?.response?.data?.message ?? null;

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-ops-card border border-ops-border bg-ops-surface" data-testid="chat-bez-dialoga">
      <div ref={okno} className="min-h-0 flex-1 overflow-y-auto bg-ops-bg px-3 py-2">
        {customerId !== null && hronika.data !== undefined && estIstoriya ? (
          <LentaHroniki
            nachalo={hronika.data}
            color="var(--ops-primary, #7c3aed)"
            klyuch={customerId}
            zagruzit={(k) => fetchHronikaKlientki(customerId, k)}
          />
        ) : (
          <p className="py-8 text-center text-ops-caption text-ops-text-secondary" data-testid="chat-net-dialoga">
            {customerId === null
              ? text("obrashcheniya.net_dialoga_bez_klientki")
              : (dannye.pochemu ?? text("obrashcheniya.net_dialoga"))}
          </p>
        )}
      </div>

      {customerId !== null && bookingId !== null && (
        <div className="border-t border-ops-border px-3 py-3" data-testid="chat-vybor-linii">
          {otkaz !== null && (
            <p className="mb-2 rounded-ops-md bg-ops-danger-soft px-3 py-2 text-ops-caption text-ops-danger" role="alert">{otkaz}</p>
          )}
          {perepiska.isLoading ? (
            <p className="text-ops-caption text-ops-text-secondary">…</p>
          ) : linii.length === 0 ? (
            <p className="text-ops-caption text-ops-text-secondary" data-testid="chat-nekuda">{text("obrashcheniya.net_dialoga_nekuda")}</p>
          ) : (
            <>
              <p className="mb-2 text-ops-caption text-ops-text-secondary">{text("obrashcheniya.net_dialoga_nachat")}</p>
              <div className="flex flex-col gap-1.5">
                {linii.map((l) => (
                  <button
                    key={l.channel_id}
                    type="button"
                    disabled={nachat.isPending}
                    onClick={() => nachat.mutate({ kind: l.kind, channel_id: l.channel_id })}
                    className="flex w-full items-center gap-2.5 rounded-ops-md border border-ops-border px-3 py-2.5 text-left text-ops-body text-ops-text hover:border-ops-primary disabled:opacity-40"
                    data-testid="chat-liniya"
                    data-channel={l.channel_id}
                  >
                    <ZnachokKanala kind={l.kind} color="var(--ops-primary, #7c3aed)" razmer={22} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-semibold">{l.nazvanie}{l.podpis !== "" ? ` · ${l.podpis}` : ""}</span>
                      {l.filial !== "" && <span className="block truncate text-ops-caption text-ops-text-secondary">{l.filial}</span>}
                    </span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
